import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Loader2, X, Zap, TrendingUp, Star, Check, ExternalLink,
  BarChart3, Eye, MousePointer, Users, Play
} from 'lucide-react';

const BUNDLE_ICONS = {
  starter: Zap,
  growth: TrendingUp,
  spotlight: Star,
};

const BUNDLE_STYLES = {
  starter: { bg: 'from-blue-600/20 to-blue-800/20', border: 'border-blue-500/30', accent: 'text-blue-400', btn: 'bg-blue-600 hover:bg-blue-700' },
  growth: { bg: 'from-amber-600/20 to-orange-800/20', border: 'border-amber-500/30', accent: 'text-amber-400', btn: 'bg-amber-600 hover:bg-amber-700' },
  spotlight: { bg: 'from-red-600/20 to-pink-800/20', border: 'border-red-500/30', accent: 'text-red-400', btn: 'bg-red-600 hover:bg-red-700' },
};

export default function PromoteModal({ isOpen, onClose, contentType, contentId, contentTitle }) {
  const { getAuthHeaders } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [connections, setConnections] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchBundles();
      fetchConnections();
    }
  }, [isOpen]);

  const fetchBundles = async () => {
    try {
      const res = await fetch(`${API}/promo/bundles`);
      if (res.ok) { const d = await res.json(); setBundles(d.bundles || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API}/social/connections`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setConnections(d.connections || []); }
    } catch {}
  };

  const handlePurchase = async (bundleId) => {
    setPurchasing(bundleId);
    try {
      const connectedPlatforms = connections
        .filter(c => c.status === 'connected' || c.status === 'smart_share')
        .map(c => c.platform);

      const res = await fetch(`${API}/promo/checkout`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bundle_id: bundleId,
          content_type: contentType,
          content_id: contentId,
          origin_url: window.location.origin,
          cross_post_platforms: connectedPlatforms,
        }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Checkout failed');
      }
    } catch { toast.error('Checkout failed'); }
    finally { setPurchasing(null); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <Card className="bg-card border-border p-0 overflow-hidden" data-testid="promote-modal">
          {/* Header */}
          <div className="p-5 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-foreground">Promote Your Content</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {contentTitle ? `Promoting: "${contentTitle}"` : 'Choose a promotion bundle'}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-promote-modal">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
            </div>
          ) : (
            <div className="p-5">
              {/* Bundle Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {bundles.map(bundle => {
                  const style = BUNDLE_STYLES[bundle.bundle_id] || BUNDLE_STYLES.starter;
                  const Icon = BUNDLE_ICONS[bundle.bundle_id] || Zap;
                  const isPopular = bundle.bundle_id === 'growth';

                  return (
                    <Card
                      key={bundle.bundle_id}
                      className={`relative bg-gradient-to-b ${style.bg} ${style.border} border p-5 flex flex-col`}
                      data-testid={`bundle-${bundle.bundle_id}`}
                    >
                      {isPopular && (
                        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-[10px] font-bold px-2">
                          MOST POPULAR
                        </Badge>
                      )}

                      <div className="flex items-center gap-2 mb-3 mt-1">
                        <Icon className={`w-5 h-5 ${style.accent}`} />
                        <h3 className="font-bold text-foreground">{bundle.name}</h3>
                      </div>

                      <div className="mb-3">
                        <span className="text-2xl font-bold text-foreground">{bundle.price_display}</span>
                        <span className="text-xs text-muted-foreground ml-1">/ {bundle.duration_display}</span>
                      </div>

                      <p className="text-xs text-muted-foreground mb-4">{bundle.description}</p>

                      <div className="space-y-2 mb-4 flex-1">
                        {bundle.placement_labels.map((label, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Check className={`w-3.5 h-3.5 ${style.accent} flex-shrink-0`} />
                            <span className="text-xs text-foreground/80">{label}</span>
                          </div>
                        ))}
                      </div>

                      <div className="text-[10px] text-muted-foreground mb-3 flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Est. {bundle.estimated_reach}
                      </div>

                      <Button
                        onClick={() => handlePurchase(bundle.bundle_id)}
                        disabled={purchasing !== null}
                        className={`w-full ${style.btn} text-white`}
                        data-testid={`buy-${bundle.bundle_id}`}
                      >
                        {purchasing === bundle.bundle_id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Zap className="w-4 h-4 mr-2" />
                        )}
                        {bundle.price_display} — Boost Now
                      </Button>
                    </Card>
                  );
                })}
              </div>

              {/* Social Sync Info */}
              {connections.length > 0 && (
                <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs text-blue-400">
                    <strong>{connections.length} connected platform(s)</strong> will auto-share your promoted content (Growth & Spotlight bundles).
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---- Compact Promote Button ----
export function PromoteButton({ contentType, contentId, contentTitle, size = 'sm' }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size={size}
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
        className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
        data-testid={`promote-btn-${contentId}`}
      >
        <Zap className="w-3.5 h-3.5 mr-1" />
        Promote
      </Button>
      <PromoteModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contentType={contentType}
        contentId={contentId}
        contentTitle={contentTitle}
      />
    </>
  );
}

// ---- Analytics Mini Card ----
export function PromoAnalyticsBadge({ analytics }) {
  if (!analytics || analytics.impressions === 0) return null;

  return (
    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{analytics.impressions}</span>
      <span className="flex items-center gap-1"><MousePointer className="w-3 h-3" />{analytics.clicks}</span>
      <span className="flex items-center gap-1"><Play className="w-3 h-3" />{analytics.plays}</span>
    </div>
  );
}
